import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowUp, Check, Loader2, Mic, Plus, Square, X } from "lucide-react";
import type { ChatAttachment } from "@/types/gateway";
import { cn } from "@/lib/utils";
import { ChatLevelChip } from "@/components/chat/ChatLevelChip";
import { ChatLevelConfigGate } from "@/components/chat/ChatLevelConfigGate";
import { ChatRescueBanner } from "@/components/chat/ChatRescueBanner";
import { useChatLevel } from "@/hooks/useChatLevel";
import { chatLevelGateInput } from "@/lib/chat-level";
import {
  DICTATION_WAVE_BARS,
  useVoiceDictation,
  type VoiceDictation,
} from "@/hooks/useVoiceDictation";
import { useComposerPrefillStore } from "@/stores/useComposerPrefillStore";
import {
  EMPTY_HISTORY,
  ensureComposerHistoryHydrated,
  useComposerHistoryStore,
} from "@/stores/useComposerHistoryStore";
import { resolveHistoryIntent } from "@/lib/workspace/composer-history";
import {
  buildSelectionDirective,
  useSelectionContextStore,
} from "@/stores/useSelectionContextStore";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useToastStore } from "@/stores/useToastStore";
import { contextUndeliveredKind } from "@/lib/chat/context-delivery";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { useUploadLimits } from "@/hooks/useUploadLimits";
import { acceptAttribute } from "@/lib/upload-safeguards";
import { UploadPreflightDialog } from "./UploadPreflightDialog";
import { useCaseWorkspaceTree } from "@/hooks/useCaseWorkspaceTree";
import {
  buildMentionsDirective,
  dedupeMentionsByPath,
  filterWorkspaceFiles,
  flattenWorkspaceTree,
  matchMentionTrigger,
  WORKSPACE_FILE_MIME,
  type MentionedFile,
} from "@/lib/workspace/mentions-directive";
import { SelectionContextChip } from "./SelectionContextChip";
import { MentionPicker } from "./MentionPicker";
import { MentionChip } from "./MentionChip";
import { AskUserWizard } from "./AskUserWizard";
import { useAskUserFromAgent } from "@/hooks/useAskUserFromAgent";
import { useAskUserRehydrate } from "@/hooks/useAskUserRehydrate";
import { useDerivedChatSessionKey } from "@/hooks/useDerivedChatSessionKey";

/** Overlay-based placeholder for the bare composer. Rotates between
 *  localized invitations with a Perplexity-style rotateX flip — fades
 *  + folds out around the horizontal axis, swaps text while edge-on,
 *  fades + folds back in. ~280ms per half-cycle, ~560ms total flip.
 *  Lighter color than the previous opaque placeholder so the rotation
 *  reads as a hint, not a typed line. */
function FlipPlaceholderOverlay({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const [displayed, setDisplayed] = useState(text);
  const [phase, setPhase] = useState<"in" | "out">("in");

  useEffect(() => {
    if (text === displayed) return;
    setPhase("out");
    const tid = window.setTimeout(() => {
      setDisplayed(text);
      setPhase("in");
    }, 280);
    return () => window.clearTimeout(tid);
  }, [text, displayed]);

  return (
    <div
      aria-hidden
      // `absolute inset-0` positions this box at the parent's padding-
      // box edge (top:0 is at the inner border edge, ABOVE the parent's
      // padding). Mirror the parent's `px-1.5 pt-2` here so the inner
      // flip element's content area matches the textarea's content area
      // — otherwise the placeholder sits ~8px above where the typed
      // text appears.
      className="pointer-events-none absolute inset-0 px-1.5 pt-2"
      style={{ perspective: "320px" }}
    >
      <div
        data-testid="placeholder-flip"
        data-phase={phase}
        className={cn(
          "transition-all duration-300 ease-in-out",
          phase === "out" ? "opacity-0" : "opacity-100",
          className,
        )}
        style={{
          transformOrigin: "center",
          transform:
            phase === "out" ? "rotateX(90deg)" : "rotateX(0deg)",
        }}
      >
        {text === "" ? " " : displayed}
      </div>
    </div>
  );
}

interface ChatInputProps {
  /** Submit the prompt. `attachments` is for AIEditor's
   *  petition-selection sidecars; `selection` is the viewer
   *  text-selection the lawyer had active when they hit send (folded
   *  onto the optimistic ChatMessage by useChatSession.send so the
   *  echo sub-line renders LIVE — and the same field is durably
   *  hydrated from server `context` on reload). Both optional; most
   *  call sites (Home, AIEditor) wrap a 1- or 2-arg callback and just
   *  ignore the 3rd arg, which is fine because they don't host a
   *  selection chip. See handoff
   *  `lexide-retire-sent-selection-store-handoff-2026-06-29.md`.
   *
   *  `mentions` (4th arg) is the parallel of `selection` for the
   *  @-mention / drag-drop workspace-file references the lawyer attached
   *  — threaded onto the optimistic ChatMessage so the bubble echoes the
   *  reference chips (live-only; the directive itself rides the invisible
   *  `runtime.system.enqueue_event` side-channel, same as selection). 4th
   *  positional (not an options object) to mirror how `selection` was
   *  added a day earlier — narrower callbacks that ignore it stay
   *  assignable. See handoff
   *  `lexide-mention-workspace-files-handoff-2026-06-29.md`. */
  onSend: (
    message: string,
    attachments?: ChatAttachment[],
    selection?: { source: string; text: string },
    mentions?: MentionedFile[],
    /** 5th positional — voice-dictation marker (auto-submitted mic
     *  recording, operator brief 2026-07-02). Narrower callbacks that
     *  ignore it stay assignable, mirroring how `mentions` was added. */
    voice?: { audioUrl?: string },
  ) => void;
  onAbort: () => void;
  isStreaming: boolean;
  disabled?: boolean;
  /**
   * `bare` drops the docked chrome (the top divider + the inner
   * bordered box) and renders just the prompt + toolbar, so a parent
   * surface — e.g. the Home `<Card variant="hero">` composer — owns the
   * rounded-2xl filled box + padding. Default (`false`) keeps the
   * bottom-docked conversation composer.
   */
  bare?: boolean;
  /**
   * Show the read-only model chip (`ModelBadge`) in the composer toolbar.
   * Defaults to `bare` — the hero composer always shows it; docked callers
   * (case chat) opt in. Leave off for the slim research/editor composers.
   */
  showModel?: boolean;
  /**
   * Render the `[+]` attach button + local attachment chips. Defaults to
   * `bare`. **Two paths**:
   *   - Without `onAttach`: selected files accumulate in local state +
   *     render as removable chips. The composer transmits text only —
   *     the chips are display-only. Used by the global / research /
   *     no-case-scope callers (legacy behaviour).
   *   - With `onAttach`: each picked file is delegated to the parent's
   *     callback (the case-workspace upload pipe — see `AIEditorPromptBox`
   *     and `useDocumentUpload`). Local chip state is bypassed; the
   *     parent owns the progress / result / error UI.
   */
  enableAttach?: boolean;
  /**
   * Show the upload PREFLIGHT dialog before the native picker (miniplan
   * Feature A, gate A-3 ★). ON by default wherever `[+]` renders, because
   * this is the ONE interception point — parents keep zero dialog knowledge,
   * and the rejected alternative (per-parent wrappers) meant two copies today
   * and N tomorrow.
   *
   * The dialog's mode follows the upload path, not this flag:
   *   - `onAttach` present → `case` mode: explains the ingest pipeline, then
   *     opens the picker from its CTA.
   *   - `onAttach` absent → `no-case` mode: explains that files attach to a
   *     case and offers nothing to pick (gate A-4 ★). This deliberately
   *     supersedes the legacy display-only chip path, which accepted a file
   *     and transmitted it NOWHERE — no upload, no error, nothing.
   *
   * Set `false` to restore the legacy direct-picker/chip behaviour. It is an
   * explicit opt-out rather than a deletion so the ruling stays reversible:
   * §2.2 records that the operator may yet prefer to keep the chips.
   */
  preflight?: boolean;
  /**
   * When `disabled` AND this is set, the box renders this message *inside*
   * the input region (in place of the editable textarea) instead of a
   * greyed "Pergunte…" placeholder. Used for the view-only permission
   * state so the user sees why typing does nothing, right where they'd
   * type — rather than a dead box with a separate notice beneath it.
   */
  disabledNotice?: string;
  /**
   * Override the textarea's placeholder. Defaults to the historic
   * "Pergunte…" (kept for case/research callers that haven't opted in).
   * The welcome surface passes a Lexiun-flavoured CTA copy here so the
   * empty-state prompt reads as an invitation rather than a generic ask.
   */
  placeholder?: string;
  /**
   * When set, the `[+]` picker delegates the selected files to the
   * parent INSTEAD of accumulating them in the local `attachments`
   * state. Parent owns the upload + result/error state. Used by the
   * case-workspace composer (AIEditorPromptBox) to wire the
   * `document.upload` RPC + the m-ingest mission trigger (per
   * `ai-docs/handoffs/lexide-upload-to-ingest-e2e-handoff.md`).
   *
   * When unset, the picker keeps the historic local-only behaviour —
   * selected files become removable chips with no upload (the legacy
   * placeholder state from before the upload-to-ingest wire).
   */
  onAttach?: (file: File) => void | Promise<void>;
  /**
   * Enable the @-mention / drag-drop workspace-file reference affordance
   * (the autocomplete picker, the drag-drop target, the chip row, and the
   * `lexide-spa:referenced-files` enqueue). Defaults `true` — the real
   * chat composers (case / global / home / research) all want it. Single-
   * purpose `bare` embeds that must NOT emit a referenced-files directive
   * (e.g. the reassess-guidance dialog) pass `false` so the picker, the
   * drag target, and the enqueue are all inert.
   */
  enableMentions?: boolean;
  /**
   * Conversation identity for prompt-history recall (ArrowUp repeats the
   * last 10 SENT prompts — `lexide-promptbox-history-nav-miniplan.md`).
   * The case layout passes its `caseId`; the home/global thread passes
   * the literal `"global"`. **Absent ⇒ the feature is entirely inert**
   * (no recording, no recall) — which is how the editor promptbox and
   * the reassess dialog stay out: their prompts are a different
   * vocabulary and would surface as bogus "recent commands" in chat
   * (⚖ D-2: histories are per conversation, never mixed).
   */
  historyScope?: string;
}

/** The composer card's width. Exported because the status chips rendered
 *  ABOVE it (upload result, selection) must line up with it — they are
 *  siblings in an unconstrained column, so without this they span the whole
 *  pane while the box below sits at 720px. One source of truth so the two
 *  cannot drift apart. */
export const COMPOSER_MAX_W = "max-w-[720px]";

/**
 * How many lines the composer grows to before it starts scrolling.
 *
 * ⚖ Operator 2026-09-08, from a screenshot of a long prompt scrolling inside a
 * one-line box: *"the promptbox should auto-grow (up) 5-10 lines to fit bigger
 * prompts before scrolling."* Ten is the top of that range — the box only
 * reaches it when the text does, and past it the textarea scrolls as before.
 *
 * ⚠ Deliberately a LINE COUNT, not a pixel ceiling. The previous cap was a
 * bare `bare ? 160 : 100` px, and this component renders THREE typography
 * variants (hero `leading-7` = 28px, rich docked = 24px, plain docked
 * `text-xs leading-relaxed` ≈ 19.5px). One px number therefore meant a
 * different number of lines in each — the docked composer was getting 4.17
 * lines, measured in the browser, which is what the operator hit.
 */
const COMPOSER_MAX_LINES = 10;

export function ChatInput({
  onSend,
  onAbort,
  isStreaming,
  disabled,
  bare = false,
  showModel = bare,
  enableAttach = bare,
  preflight = true,
  disabledNotice,
  placeholder = "Pergunte...",
  onAttach,
  enableMentions = true,
  historyScope,
}: ChatInputProps) {
  // Server-owned upload policy; drives the picker's accept list.
  const { t } = useTranslation();
  const { limits: uploadLimits } = useUploadLimits();
  // `lx_ask_user` (Phase A) — subscribes to the session.tool broadcast. See
  // the `askUserCard` element below for why the mount lives in this shared
  // component rather than in each composer host.
  // Scoped to THIS composer's session — an ask belonging to another case must
  // not render here, or its answers would post into the wrong conversation.
  // Derived the same way `AIEditorPromptBox` derives it, so the composer and
  // the ask agree on what "this conversation" means.
  const composerSessionKey = useDerivedChatSessionKey();

  // P4 — the concierge FAST/NORMAL/DEEP seam (one hook instance per composer;
  // the chip and the L-9 gate share it, so a definitive failure from a pick
  // reaches the same state the gate reads). Keyed on `showModel`: the slim
  // research/editor composers (L-6's ruled scope) never poll the seam.
  const chatLevel = useChatLevel(showModel ? composerSessionKey : null);
  const levelGate = chatLevelGateInput(chatLevel.state, chatLevel.definitiveFail);
  const askUser = useAskUserFromAgent(composerSessionKey);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // === Prompt-history recall (miniplan lexide-promptbox-history-nav) ===
  // The durable list lives in the store; NAVIGATION state is this
  // composer's alone. `historyIndex === null` ⇒ not navigating.
  // `savedDraftRef` holds the in-progress draft snapshotted when
  // navigation begins, restored on Escape / walking past newest.
  const promptHistory = useComposerHistoryStore((s) =>
    historyScope ? (s.history[historyScope] ?? EMPTY_HISTORY) : EMPTY_HISTORY,
  );
  const recordPrompt = useComposerHistoryStore((s) => s.recordPrompt);
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);
  const savedDraftRef = useRef("");
  /** Render a recalled entry (or the restored draft) with the caret at
   *  the end. Deliberately does NOT run the mention-trigger re-eval —
   *  a recalled prompt containing a raw `@token` must not pop the
   *  picker; only live typing does that (`handleChange`). */
  const applyRecalledText = useCallback((value: string) => {
    setText(value);
    queueMicrotask(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(value.length, value.length);
      }
    });
  }, []);

  // Voice dictation (operator brief 2026-07-02, ChatGPT-style flow):
  // mic → live waveform strip in place of the textarea → ✗ discards /
  // ✓ transcribes via the runtime's LOCAL lx-stt → transcript lands in
  // the composer for review before send.
  const dictation = useVoiceDictation();
  // "error" is ACTIVE too — the strip must stay mounted to SHOW the
  // failure (mic denied / STT timeout) with its dismiss ✗. Excluding it
  // was the operator-reported "Transcrevendo… then simply disappeared"
  // bug (2026-07-02): a transcribe failure unmounted the strip silently.
  const dictationActive = dictation.state !== "idle";
  // ✓ = ONE step (operator brief 2026-07-02, 2nd refinement): transcribe
  // AND auto-submit — the transcript goes straight out as a voice-styled
  // turn (VOZ bubble + session-local playback via `voice.audioUrl`), no
  // review stop in the composer. Fallbacks that DO land in the composer
  // instead (so nothing is ever lost): a stream already in flight, a
  // disabled composer, or a caller whose narrower onSend can't accept
  // sends mid-stream (isStreaming guards the direct submit).
  const handleDictationAccept = useCallback(async () => {
    const { transcript, audioUrl } = await dictation.accept();
    if (!transcript) return; // failure already surfaced on the strip
    if (disabled || isStreaming) {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setText((prev) => (prev.trim() ? `${prev.replace(/\s+$/, "")} ${transcript}` : transcript));
      queueMicrotask(() => textareaRef.current?.focus());
      return;
    }
    // ⚖ D-3: dictation sends ARE prompts the lawyer composed — they
    // enter history like typed ones (this is the second send door).
    if (historyScope) recordPrompt(historyScope, transcript);
    onSend(transcript, undefined, undefined, undefined, {
      ...(audioUrl ? { audioUrl } : {}),
    });
  }, [dictation, disabled, isStreaming, onSend, historyScope, recordPrompt]);

  // === @-mention / drag-drop workspace-file references (handoff
  // `lexide-mention-workspace-files-handoff-2026-06-29.md`) ===
  // `mentions` is an ORDERED, path-deduped list of referenced files. The
  // picker (`mentionTrigger !== null`) opens when the lawyer types
  // `@<filter>` at the caret; `mentionActiveIndex` is the keyboard
  // highlight. `isDragOver` paints the gold drop-target ring while a
  // sidebar file is dragged over the textarea.
  const [mentions, setMentions] = useState<MentionedFile[]>([]);
  const [mentionTrigger, setMentionTrigger] = useState<{
    filter: string;
    atIndex: number;
  } | null>(null);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);

  // The autocomplete corpus — the SAME `case.workspace_tree` React-Query
  // cache the sidebar `CaseFileNav` consumes (no second fetch; enabled-
  // gated + scope-distinct keyed). Flattened to a file list once per tree.
  const { data: workspaceTree } = useCaseWorkspaceTree();
  const allFiles = useMemo(
    () => (workspaceTree ? flattenWorkspaceTree(workspaceTree.root) : []),
    [workspaceTree],
  );
  const mentionResults = useMemo(
    () => (mentionTrigger ? filterWorkspaceFiles(allFiles, mentionTrigger.filter, 8) : []),
    [allFiles, mentionTrigger],
  );
  // The picker only renders when the feature is enabled, there's a live
  // trigger, AND the workspace actually has files (no "No files match"
  // flash on an empty/unscoped workspace tree).
  const pickerOpen = enableMentions && mentionTrigger !== null && allFiles.length > 0;
  // Stable id for the combobox/listbox ARIA relationship (review finding
  // [4]). The active option's id drives the textarea's aria-activedescendant
  // so screen readers announce the keyboard-highlighted file.
  const mentionListboxId = useId();
  const activeOptionId =
    pickerOpen && mentionResults.length > 0
      ? `${mentionListboxId}-opt-${mentionActiveIndex}`
      : undefined;

  // One-shot seed from the composer-prefill store — any in-app surface
  // (the source-assessment pane's "Run verify" CTA, etc.) can drop a
  // directive into the composer without prop-threading. Consumed on
  // mount AND on subsequent writes; the store clears itself after each
  // consume so the prefill never replays.
  const pendingPrefill = useComposerPrefillStore((s) => s.pendingText);
  const consumePrefill = useComposerPrefillStore((s) => s.consume);
  useEffect(() => {
    if (pendingPrefill === null) return;
    const seed = consumePrefill();
    if (seed) {
      setText(seed);
      // Focus the textarea so the user can edit or send immediately.
      textareaRef.current?.focus();
    }
  }, [pendingPrefill, consumePrefill]);

  // `rich` = the Claude-style toolbar layout (attach + model chip + send on
  // their own row). `prominent` drives the larger prompt sizing. Both the
  // hero (`bare`) and any docked caller that opts into model/attach get it;
  // the plain docked composer (research/editor) stays exactly as before.
  const rich = showModel || enableAttach;
  const prominent = bare || rich;

  // Auto-resize after every render (text change) — avoids React style prop
  // overriding imperative height.
  //
  // The cap is DERIVED from the element's real line box rather than hardcoded
  // per mode, so all three typography variants get the same number of lines
  // (see `COMPOSER_MAX_LINES`). `useLayoutEffect` runs before paint, so this
  // owns `maxHeight` too — keeping it here rather than in a `style` prop means
  // the growth cap and the scroll ceiling cannot drift apart, which the old
  // "the grow cap matches the CSS maxHeight per mode" comment was asking a
  // human to guarantee across two separate literals.
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const cs = getComputedStyle(ta);
    const line = Number.parseFloat(cs.lineHeight);
    // `line-height: normal` parses to NaN — fall back to the pre-2026-09-08
    // per-mode ceilings rather than letting the box grow unbounded.
    const cap =
      Number.isFinite(line) && line > 0
        ? line * COMPOSER_MAX_LINES +
          Number.parseFloat(cs.paddingTop) +
          Number.parseFloat(cs.paddingBottom)
        : bare
          ? 160
          : 100;
    ta.style.maxHeight = `${cap}px`;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, cap)}px`;
  }, [text, bare]);

  const selection = useSelectionContextStore((s) => s.selection);
  const clearSelection = useSelectionContextStore((s) => s.clear);
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const selectionScope = useActiveContextStore((s) => s.selection);

  // ⚖ Reload survival (ui-tester I-…-4). A live broadcast always wins; this
  // only fills the gap a reload leaves, and ONLY for a park whose answers have
  // a door — `answerable` is false for an agent (handback) park, which must not
  // take over the composer because the takeover would remove the lawyer's only
  // working recovery (asking the assistant to repeat the questions).
  const rehydratedAsk = useAskUserRehydrate(composerSessionKey);
  // ⚖ R1 — `overAskCap` gates the WIZARD, never the questions. A rebuilt ask with
  // more questions than the per-ask cap still renders (as the read-only notice
  // below); what it must not do is mount the wizard, whose takeover is total and
  // whose cap is a ratified anti-wall-of-text rule. See `RehydratedAsk.overAskCap`.
  const effectiveAskCard =
    askUser.card ??
    (rehydratedAsk?.answerable && !rehydratedAsk.overAskCap ? rehydratedAsk.card : null);

  // Deferred history hydration — MUST wait for identity (see the store's
  // skipHydration note: an eager read pre-auth is refused by the tenant
  // wrapper AND deletes the payload, which for this non-cache store is
  // the data). "connected" ⇒ auth resolved ⇒ the stamp check can pass.
  useEffect(() => {
    if (historyScope && connectionState === "connected") {
      ensureComposerHistoryHydrated();
    }
  }, [historyScope, connectionState]);
  // Re-entrancy latch — `handleSend` is `async` (awaits the
  // selection-context enqueue before firing chat.send). The
  // synchronous-disabled-prop guard below doesn't cover the await
  // window, so a double-Enter / double-click within ~10-50ms would
  // fire twice. This ref-based latch rejects re-entry while busy.
  const sendingRef = useRef(false);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || disabled || sendingRef.current) return;
    sendingRef.current = true;

    // Snapshot the selection + mentions before we clear the chips so the
    // rest of the function can act on them even after the synchronous
    // "instant feedback" clears below.
    const sentSelection = selection;
    const sentMentions = mentions;
    // The enqueue endpoint requires full case ids (it builds the session
    // via buildLxSessionKey({type:"case", …})). `scopeReady` is the
    // shared precondition; selection + mentions each gate their own
    // enqueue on it (plus having something to send).
    const scopeReady = Boolean(
      rpc &&
        connectionState === "connected" &&
        selectionScope?.clientId &&
        selectionScope?.matterId &&
        selectionScope?.caseId,
    );
    const canEnqueueSelection = Boolean(sentSelection) && scopeReady;
    const canEnqueueMentions = enableMentions && sentMentions.length > 0 && scopeReady;

    // === INSTANT VISUAL FEEDBACK (operator brief 2026-06-28 6th
    // refinement — issue 2) ===
    // Prompt-history record — THIS is the typed-send door, deliberately
    // (with the dictation door below the only other one). ⚠ Do not
    // "unify" the AskUserCard's submit through here: the card calls the
    // `onSend` PROP directly BY DESIGN, so chip answers never enter
    // history — a recalled "[respostas] • …" is not a command the
    // lawyer would re-send (verified by `mission-ingest`, bus thread
    // 2026-08-17). Prefill directives are covered for free: they only
    // reach this line if the lawyer actually sent them.
    if (historyScope) recordPrompt(historyScope, trimmed);
    setHistoryIndex(null);

    // Clear the textarea + selection chip + browser-native selection
    // BEFORE the async `await rpc...` window so the user gets an
    // immediate "I clicked it" response. Previously these happened
    // AFTER the await, so a 50-300ms rpc latency presented as a
    // dead button.
    setText("");
    if (sentSelection) {
      clearSelection();
      // Remove the browser's native ::selection highlight too —
      // otherwise the user sees the gold custom Highlight vanish
      // (our store cleared) but the blue native selection lingers
      // until they click somewhere.
      window.getSelection()?.removeAllRanges();
    }
    // Clear the mention chips + close any open picker (parity with the
    // selection chip's instant clear). Mentions don't survive a turn —
    // next-turn references are a re-mention (handoff §7 non-goal).
    if (sentMentions.length > 0) setMentions([]);
    setMentionTrigger(null);
    // === Echo annotation (handoff lexide-retire-sent-selection-store
    // 2026-06-29) ===
    // The selection is threaded as the 3rd positional arg of onSend
    // (→ useChatSession.send → useChatStore.addUserMessage) so it
    // lands on the optimistic ChatMessage directly. Replaces the
    // prior in-memory `useSentSelectionStore.push({content,...})`
    // bridge that was content-keyed + lost on reload.
    // `UserMessage` reads `message.selection` (server-hydrated on
    // reload, live-stamped here) — single source of truth, no FIFO.

    try {
      // Selection-as-context (Phase 2, 2026-06-28): if the lawyer
      // has selected text in a viewer (chip was visible), fire the
      // directive as an INVISIBLE system event via
      // `runtime.system.enqueue_event` BEFORE `onSend` (the
      // chat.send path). The enqueue is AWAITED so the runtime has
      // queued the event before the chat turn enters the LLM —
      // without the await, chat.send races the enqueue and the
      // agent's first turn sees only the user prompt.
      //
      // Selection text is USER-CONTROLLED — wrapped in datamarked
      // tags by `buildSelectionDirective` so the model treats it as
      // a reference, not an instruction (OWASP LLM01 spotlighting).
      // The visible chat message stays just the user's typed prompt.
      //
      // Scope caveat: the endpoint requires case ids (handler builds
      // the session via buildLxSessionKey({type:"case", …})), so in
      // non-case scopes (the global/org-scope chat) there is no
      // enqueue. Future: a sibling endpoint for org-scope sessions
      // (deferred to Phase 3 runtime handoff).
      //
      // Delivery tracking (F2, widened 2026-08-24 — see
      // lib/chat/context-delivery.ts): the echo and the honesty signal
      // key on whether THIS enqueue actually succeeded — set at the one
      // success point, false through the guard AND the catch — never on
      // scope. Before this, a skipped OR failed enqueue still rendered
      // the Quote echo on the sent bubble: durably affirmed context loss.
      let selectionDelivered = false;
      let mentionsDelivered = false;
      if (canEnqueueSelection && sentSelection) {
        const payload = buildSelectionDirective(sentSelection);
        try {
          await rpc!.runtimeSystemEnqueueEvent({
            clientId: selectionScope!.clientId!,
            matterId: selectionScope!.matterId!,
            caseId: selectionScope!.caseId!,
            text: payload,
            source: "lexide-spa:selection-context",
          });
          selectionDelivered = true;
        } catch (err) {
          // Degrade — fire the user message anyway. The agent just
          // won't have the selection context this turn; the toast below
          // says so, and the echo is withheld rather than forged.
          console.warn(
            "[selection-context] runtime.system.enqueue_event failed; sending prompt without selection context",
            err,
          );
        }
      }
      // @-mention / drag-drop references — the THIRD instance of the
      // rich-context-via-directive pattern. Same AWAITED-before-onSend
      // shape as the selection enqueue: queue the `<user_referenced_files>`
      // block so the runtime has it before the chat turn enters the LLM.
      // The paths are USER-CONTROLLED — datamarked by
      // `buildMentionsDirective` (OWASP LLM01 spotlighting). Degrades
      // silently in non-case scope (no caseId → no enqueue; the chips
      // were already cleared so the user saw their references land).
      if (canEnqueueMentions) {
        const directive = buildMentionsDirective(sentMentions);
        try {
          await rpc!.runtimeSystemEnqueueEvent({
            clientId: selectionScope!.clientId!,
            matterId: selectionScope!.matterId!,
            caseId: selectionScope!.caseId!,
            text: directive,
            source: "lexide-spa:referenced-files",
          });
          mentionsDelivered = true;
        } catch (err) {
          console.warn(
            "[mentions] runtime.system.enqueue_event failed; sending prompt without file references",
            err,
          );
        }
      }
      // Honesty signal for whatever did NOT reach the runtime: one warn
      // toast, cause-blind (org-scope skip and case-scope transport
      // failure are the same fact to the lawyer: the assistant did not
      // get it). Pre-localized text per the toast store's contract.
      const undelivered = contextUndeliveredKind({
        selectionSent: Boolean(sentSelection),
        selectionDelivered,
        mentionsSent: sentMentions.length > 0,
        mentionsDelivered,
      });
      if (undelivered) {
        useToastStore.getState().addToast({
          text: t(`chat.contextUndelivered.${undelivered}`),
          kind: "warn",
          autoDismissMs: 6000,
        });
      }
      // Thread `sentSelection` (3rd arg) + `sentMentions` (4th arg) —
      // both falsy/empty when there was no chip, and BOTH gated on
      // DELIVERY: an echo on the sent bubble is a claim the model
      // received the context, so an undelivered selection/mention set
      // renders no echo (matching the durable truth — the runtime only
      // persists context it actually received, so reload never showed
      // the forged echo either).
      onSend(
        trimmed,
        undefined,
        sentSelection && selectionDelivered
          ? { source: sentSelection.source, text: sentSelection.text }
          : undefined,
        sentMentions.length > 0 && mentionsDelivered ? sentMentions : undefined,
      );
    } finally {
      sendingRef.current = false;
    }
  }, [text, disabled, onSend, selection, clearSelection, mentions, enableMentions, rpc, connectionState, selectionScope, historyScope, recordPrompt, t]);

  // Detect / clear the @-mention trigger from the text up to the caret.
  // Called from BOTH onChange (typing) AND onSelect (caret-only moves:
  // arrow keys, Home/End, mouse clicks) so the picker closes the moment
  // the caret leaves the `@token` — without onSelect the dropdown would
  // float stale over a moved caret and `insertMention` could excise at the
  // wrong index (review finding [1]). Idempotent: same trigger → no churn.
  const reevaluateMentionTrigger = useCallback(
    (value: string, caret: number) => {
      if (!enableMentions) return;
      const trig = matchMentionTrigger(value.slice(0, caret));
      setMentionTrigger((prev) => {
        if (!trig) return null;
        if (prev && prev.atIndex === trig.atIndex && prev.filter === trig.filter) return prev;
        return trig;
      });
      if (trig) setMentionActiveIndex(0);
    },
    [enableMentions],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      // Editing EXITS history navigation (the recalled entry becomes an
      // ordinary draft; the next ArrowUp starts from the top again —
      // shell behaviour). Real user input only reaches this handler via
      // DOM events; programmatic recall calls setText directly, so no
      // latch is needed to tell the two apart.
      setHistoryIndex((i) => (i === null ? i : null));
      setText(value);
      reevaluateMentionTrigger(value, e.target.selectionStart ?? value.length);
    },
    [reevaluateMentionTrigger],
  );

  // Commit a picked file: drop the `@filter` token from the buffer (the
  // chip lives OUTSIDE the textarea, Claude-Code style), push the ref into
  // `mentions` (path-deduped), close the picker, restore the caret to the
  // excision point.
  const insertMention = useCallback(
    (file: MentionedFile) => {
      setMentions((m) => dedupeMentionsByPath([...m, file]));
      setMentionTrigger(null);
      const ta = textareaRef.current;
      const caret = ta?.selectionStart ?? text.length;
      const at = mentionTrigger?.atIndex ?? caret;
      // Guard against a moved caret: only excise the [at, caret) span when
      // it is still the live `@token` (starts with "@"). If the caret has
      // jumped elsewhere since the trigger opened, append the chip WITHOUT
      // touching the buffer rather than deleting unrelated text (review
      // finding [1] — the data-loss-grade case).
      if (!text.slice(at, caret).startsWith("@")) return;
      const before = text.slice(0, at);
      const after = text.slice(caret);
      setText(before + after);
      // Restore the caret to the excision point on the next microtask
      // (after the controlled value re-render). Best-effort — focus stays
      // in the box so the lawyer can keep typing.
      queueMicrotask(() => {
        if (ta) {
          ta.focus();
          ta.setSelectionRange(before.length, before.length);
        }
      });
    },
    [text, mentionTrigger],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Picker keyboard nav takes precedence over send while open.
      if (pickerOpen) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionActiveIndex((i) => Math.min(i + 1, Math.max(mentionResults.length - 1, 0)));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionActiveIndex((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setMentionTrigger(null);
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          // Absorb Enter/Tab UNCONDITIONALLY while the picker is open — even
          // with zero matches — so a stray `@token` never falls through to
          // handleSend and gets sent as a malformed message (review
          // finding [6]). No match → just close the picker; the lawyer can
          // edit the token or press Enter again (picker now closed) to send.
          e.preventDefault();
          if (mentionResults.length > 0) {
            insertMention(mentionResults[mentionActiveIndex] ?? mentionResults[0]);
          } else {
            setMentionTrigger(null);
          }
          return;
        }
      }

      // === Prompt-history recall (miniplan lexide-promptbox-history-nav) ===
      // Sits AFTER the picker block on purpose: the picker owns the
      // arrows while open. `resolveHistoryIntent` returns "none" for
      // every caret-movement case (mid-text ArrowUp, IME composition,
      // uncollapsed selection) — and "none" means we do NOT
      // preventDefault, so the arrows' default caret job survives.
      if (historyScope && promptHistory.length > 0) {
        const ta = e.currentTarget;
        const intent = resolveHistoryIntent({
          key: e.key,
          isComposing: e.nativeEvent.isComposing,
          pickerOpen,
          navigating: historyIndex !== null,
          selectionStart: ta.selectionStart ?? 0,
          selectionEnd: ta.selectionEnd ?? 0,
          value: text,
        });
        if (intent === "older") {
          e.preventDefault();
          if (historyIndex === null) {
            // Entering navigation — snapshot the in-progress draft.
            savedDraftRef.current = text;
            setHistoryIndex(0);
            applyRecalledText(promptHistory[0]);
          } else if (historyIndex < promptHistory.length - 1) {
            const next = historyIndex + 1;
            setHistoryIndex(next);
            applyRecalledText(promptHistory[next]);
          }
          // At the oldest entry: swallowed (we are "in history"; letting
          // the caret jump to line start here would be noise).
          return;
        }
        if (intent === "newer") {
          e.preventDefault();
          if (historyIndex === 0) {
            // Walking past the newest entry restores the saved draft.
            setHistoryIndex(null);
            applyRecalledText(savedDraftRef.current);
          } else if (historyIndex !== null) {
            const next = historyIndex - 1;
            setHistoryIndex(next);
            applyRecalledText(promptHistory[next]);
          }
          return;
        }
        if (intent === "exit") {
          e.preventDefault();
          setHistoryIndex(null);
          applyRecalledText(savedDraftRef.current);
          return;
        }
      }

      // Backspace at the very start of an empty textarea removes the LAST
      // mention chip (Claude-Code parity).
      if (
        e.key === "Backspace" &&
        text === "" &&
        mentions.length > 0 &&
        (e.currentTarget.selectionStart ?? 0) === 0
      ) {
        e.preventDefault();
        setMentions((m) => m.slice(0, -1));
        return;
      }

      // Submit on plain Enter (canonical) or Ctrl/Cmd+Enter (alias for
      // users with muscle memory from ChatGPT / Claude.ai / Cursor).
      // Shift+Enter inserts a newline (default textarea behaviour).
      const isPlainEnter = e.key === "Enter" && !e.shiftKey && !(e.ctrlKey || e.metaKey);
      const isModifierEnter = e.key === "Enter" && (e.ctrlKey || e.metaKey);
      if (isPlainEnter || isModifierEnter) {
        e.preventDefault();
        if (isStreaming) return;
        handleSend();
      }
    },
    [handleSend, isStreaming, pickerOpen, mentionResults, mentionActiveIndex, insertMention, text, mentions, historyScope, promptHistory, historyIndex, applyRecalledText],
  );

  // Drag-drop: accept ONLY our workspace-file MIME (a sidebar file row
  // dragged onto the composer); ignore OS file / plain-text drops. Inert
  // when the feature is disabled (e.g. the reassess dialog, finding [7]).
  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLTextAreaElement>) => {
      if (!enableMentions) return;
      if (e.dataTransfer.types.includes(WORKSPACE_FILE_MIME)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setIsDragOver(true);
      }
    },
    [enableMentions],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLTextAreaElement>) => {
      if (!enableMentions) return;
      setIsDragOver(false);
      const raw = e.dataTransfer.getData(WORKSPACE_FILE_MIME);
      if (!raw) return;
      e.preventDefault();
      try {
        const f = JSON.parse(raw) as Partial<MentionedFile>;
        if (f?.path && f?.name) {
          setMentions((m) => dedupeMentionsByPath([...m, { path: f.path!, name: f.name! }]));
        }
      } catch {
        /* not our payload / malformed — ignore */
      }
    },
    [enableMentions],
  );

  const iconSize = prominent ? 18 : 14;

  // Send control — Claude-style: a circular GOLD button with an up-arrow
  // (the operator's brand-accent send). Muted circle when there's nothing
  // to send; a dark circle with a stop glyph while streaming.
  const sendButton = isStreaming ? (
    <button
      type="button"
      onClick={onAbort}
      className="shrink-0 rounded-full bg-coffee-800 p-1.5 text-cream-100 transition-colors hover:bg-coffee-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:bg-cream-200 dark:text-coffee-900 dark:hover:bg-cream-100 dark:focus-visible:ring-offset-coffee-900"
      title="Parar geração"
    >
      <Square size={iconSize} />
    </button>
  ) : (
    <button
      type="button"
      onClick={handleSend}
      disabled={disabled || !text.trim()}
      className={cn(
        "shrink-0 rounded-full p-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-coffee-900",
        text.trim() && !disabled
          ? "bg-gold-500 text-coffee-900 hover:bg-gold-600"
          : "bg-cream-200 text-cream-500 dark:bg-coffee-800 dark:text-coffee-500",
      )}
      title="Enviar mensagem"
    >
      <ArrowUp size={iconSize} />
    </button>
  );

  // Mic affordance — starts the dictation flow. Hidden while already
  // recording/transcribing (the strip owns the controls then) and when
  // the browser can't record.
  const micButton =
    dictation.supported && !dictationActive ? (
      <button
        type="button"
        data-testid="composer-mic"
        onClick={() => void dictation.start()}
        disabled={disabled || isStreaming}
        title="Ditar por voz"
        aria-label="Ditar por voz"
        className="shrink-0 rounded-full p-1.5 text-cream-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 disabled:opacity-50 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200 dark:focus-visible:ring-offset-coffee-900"
      >
        <Mic size={iconSize} />
      </button>
    ) : null;

  // `[+]` attach affordance — opens a native picker. Without `onAttach`
  // (legacy callers), selected files become local removable chips for
  // display only. With `onAttach` (case-workspace upload pipe), each
  // picked file is delegated to the parent's callback.
  const attachButton = enableAttach ? (
    <>
      <input
        ref={fileInputRef}
        type="file"
        // The server's OWN accept list (`document.upload_limits.
        // acceptedExtensions`, derived from its route table) so the picker
        // can never drift from what the runtime accepts. NOT the cap-table
        // keys: a type with no explicit cap rides on defaultMaxBytes and has
        // no `fileSizeBytes` entry, so deriving from the caps silently hid
        // .txt/.md/.odt/.rtf for weeks (fixed 2026-07-30; older runtimes
        // without the field fall back to that historical derivation). This
        // was once hardcoded `application/pdf` — the UI as the narrowest part
        // of the pipe, twice. Falls back to a wide-open picker while the
        // policy is loading — the server still enforces, so a stale `accept`
        // cannot let anything through.
        accept={onAttach ? acceptAttribute(uploadLimits) : undefined}
        // v1 upload scope is single PDF per
        // `ai-docs/handoffs/lexide-upload-to-ingest-e2e-handoff.md` §7
        // (multi-file concierge census is deferred). Legacy chip path
        // keeps multi-select for the display-only attachment list.
        multiple={!onAttach}
        className="hidden"
        data-testid="composer-file-input"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          if (picked.length) {
            if (onAttach) {
              // Parent-owned upload path — fire-and-forget per file so
              // the parent's mutation hook drives status; the local
              // `attachments` state is bypassed entirely. v1 single-
              // file scope means `picked.length` is always 1 here, but
              // we iterate defensively in case a future caller flips
              // multi-select back on.
              for (const f of picked) {
                void onAttach(f);
              }
            } else {
              // Legacy local-only behaviour for non-upload callers.
              setAttachments((prev) => [...prev, ...picked]);
            }
          }
          e.target.value = ""; // allow re-picking the same file
        }}
      />
      <button
        type="button"
        data-testid="composer-attach"
        data-preflight={preflight ? "true" : "false"}
        // Preflight intercepts here (A-3): ONE point, so no parent needs to
        // know the dialog exists. With it off we fall through to the historic
        // direct picker.
        onClick={() => {
          if (preflight && onAttach) setPreflightOpen(true);
          else fileInputRef.current?.click();
        }}
        disabled={disabled}
        title="Anexar documento"
        aria-label="Anexar documento"
        className="shrink-0 rounded-lg p-1.5 text-cream-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 disabled:opacity-50 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200 dark:focus-visible:ring-offset-coffee-900"
      >
        <Plus size={18} />
      </button>
      {preflight && onAttach && (
        <UploadPreflightDialog
          open={preflightOpen}
          onOpenChange={setPreflightOpen}
          // The mode follows the REAL capability, never the flag: with no
          // `onAttach` there is no upload pipe, and a dialog describing the
          // ingest pipeline there would describe something that cannot
          // happen.
          mode="case"
          onChooseFiles={() => {
            setPreflightOpen(false);
            fileInputRef.current?.click();
          }}
        />
      )}
    </>
  ) : null;

  // Local attachment chips (filename + remove). Pure UI — not yet sent.
  const chipsRow =
    attachments.length > 0 ? (
      <div className="flex flex-wrap gap-1.5" data-testid="composer-attachments">
        {attachments.map((f, i) => (
          <span
            key={`${f.name}-${i}`}
            className="inline-flex items-center gap-1 rounded-md border border-cream-200/60 bg-cream-50 px-2 py-0.5 text-caption text-coffee-600 dark:border-coffee-700/60 dark:bg-coffee-800 dark:text-cream-400"
          >
            <span className="max-w-[12rem] truncate">{f.name}</span>
            <button
              type="button"
              onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
              aria-label={`Remover ${f.name}`}
              className="shrink-0 rounded text-cream-500 transition-colors hover:text-coffee-700 dark:hover:text-cream-200"
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
    ) : null;

  // Referenced-file chips (@-mention / drag-drop). Rendered ABOVE the
  // textarea + below the selection chip, mirroring the attachment-chip
  // row. Each chip removes via × (or Backspace-at-start, see handleKeyDown).
  const mentionChipsRow =
    mentions.length > 0 ? (
      <div className="flex flex-wrap gap-1.5" data-testid="composer-mentions">
        {mentions.map((m) => (
          <MentionChip
            key={m.path}
            name={m.name}
            path={m.path}
            tag={m.tag}
            title={m.title}
            onRemove={() => setMentions((prev) => prev.filter((x) => x.path !== m.path))}
          />
        ))}
      </div>
    ) : null;

  // ⚖ The `lx_ask_user` card (Phase A) — the concierge's structured ask.
  //
  // Mounted HERE, in the shared composer, rather than per-layout: there are
  // THREE composer hosts (case workspace via AIEditorPromptBox, global
  // conversation, home) and a per-layout mount silently dropped the ask in two
  // of them. That failure is the worst this surface can have — the tool does
  // NOT wait (A-0.2), so the model ends its turn believing it asked while the
  // lawyer sees nothing at all. One mount makes it structurally impossible.
  //
  // ⚖ RATIFIED REDESIGN (lexide-ask-user-wizard-redesign-miniplan.md; the
  // living contract is lexide-ask-user-guide.md §2-§3): the ask TAKES OVER
  // the composer slot — while active, the textarea/attach/mic/send are
  // UNMOUNTED so exactly one send affordance exists (operator ruling,
  // verbatim: "the wizard should replace promptbox when active so user
  // cannot enter anything on the promptbox"). The agent is still not
  // blocked (A-0.2): Esc / "Responder depois" PARK the ask into a pill
  // and return the composer; only ✕ dismisses. ANTI-YANK: an ask arriving
  // over a non-empty draft or live dictation spawns PARKED — it never
  // steals the slot mid-thought.
  //
  // 🚨 REMOUNT PER ASK — measured, not precautionary (see the wizard's
  // key): keying on question ids let a second ask inherit the first's
  // picks; the key must identify the ASK (`toolCallId`).
  const [seenAskId, setSeenAskId] = useState<string | null>(null);
  const [parkedAskId, setParkedAskId] = useState<string | null>(null);
  // ⚠ Keyed on the EFFECTIVE card, not the live one. The takeover and the
  // wizard must agree about which ask is up: gating the host on the effective
  // card while leaving this on the live one would render a rehydrated wizard
  // with the composer STILL MOUNTED — two send affordances at once, which the
  // guide's takeover rule forbids in as many words.
  if (effectiveAskCard && (effectiveAskCard.askId ?? null) !== seenAskId) {
    // Render-time adjustment (React's adjust-state-during-render pattern)
    // so the anti-yank decision applies on the ask's FIRST paint — an
    // effect would flash a takeover before parking it.
    setSeenAskId(effectiveAskCard.askId ?? null);
    setParkedAskId(
      text.trim() !== "" || dictationActive ? (effectiveAskCard.askId ?? null) : null,
    );
  }
  const wizardActive =
    Boolean(effectiveAskCard) && (effectiveAskCard?.askId ?? null) !== parkedAskId;
  const focusComposerSoon = () =>
    queueMicrotask(() => textareaRef.current?.focus());

  // Focus must FOLLOW the takeover, not just the first mount: the wizard
  // focuses itself on MOUNT, but a pill-resume only unhides the host
  // (hidden-mount preserves picks, so there is no remount) — leaving focus
  // outside the wizard, where Esc silently does nothing until the lawyer
  // clicks inside (measured live 2026-08-18, guide §2 Esc contract).
  const askWizardHostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!wizardActive) return;
    askWizardHostRef.current
      ?.querySelector<HTMLElement>('[data-testid="ask-user-wizard"]')
      ?.focus();
  }, [wizardActive]);

  const askUserWizard = effectiveAskCard ? (
    <AskUserWizard
      key={effectiveAskCard.askId}
      data={effectiveAskCard}
      onSubmit={(answersMessage) => {
        // Posts through the composer's OWN onSend, so the optimistic bubble
        // and turn lifecycle are identical to a typed reply. Deliberately
        // NOT through handleSend: chip answers must not enter the prompt
        // HISTORY (verified by construction with mission-ingest 2026-08-17).
        onSend(answersMessage);
        askUser.clear();
        focusComposerSoon();
      }}
      onPark={() => {
        setParkedAskId(effectiveAskCard?.askId ?? null);
        focusComposerSoon();
      }}
    />
  ) : null;

  // The parked state: composer back, the ask one tap away. ⚠ PICKS SURVIVE
  // PARKING because the wizard stays MOUNTED while parked — hidden via
  // CSS (`hidden`), never unmounted (unmounting would discard the keyed
  // instance's state and break the plan's "picks preserved" promise). The
  // takeover contract (S1: composer absent from the DOM) applies to the
  // ACTIVE state only; a hidden wizard node while parked violates nothing.
  const askUserWizardHost = effectiveAskCard ? (
    <div
      ref={askWizardHostRef}
      data-testid="ask-user-wizard-host"
      className={wizardActive ? undefined : "hidden"}
    >
      {askUserWizard}
    </div>
  ) : null;

  // ⚖ A park the wizard does not take over still has to be visible after a
  // reload (ui-tester I-…-4). This is the non-takeover half: the questions come
  // back, the composer stays mounted, and there is deliberately NO send control.
  //
  // TWO different parks reach it, for OPPOSITE reasons:
  //   • a `handback` park — no door a human can use (§7-bis), so a send control
  //     would be a lie, and a takeover would remove the one recovery that works
  //     today (asking the assistant to repeat them);
  //   • ⚖ R1 — an ANSWERABLE park carrying more questions than the wizard's
  //     per-ask cap. Here the lawyer CAN answer; only the wizard is withheld,
  //     because its cap is a ratified C-1 rule and its takeover is total.
  //
  // ⚠ So the copy BRANCHES and the two arms must not be merged — the first says
  // the questions cannot be answered here, which is false for the second.
  //
  // Copy follows the sibling wizard's convention: hardcoded pt-BR, "assistente"
  // (never "agente"/"bot").
  const parkedAskNotice =
    !effectiveAskCard &&
    rehydratedAsk &&
    (!rehydratedAsk.answerable || rehydratedAsk.overAskCap) ? (
      <div
        data-testid="ask-user-parked-notice"
        data-park-kind={rehydratedAsk.parkKind}
        data-over-ask-cap={rehydratedAsk.overAskCap ? "1" : undefined}
        className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
      >
        <div className="font-semibold">Perguntas registradas pelo assistente</div>
        <ol className="mt-1 list-decimal space-y-0.5 pl-4">
          {rehydratedAsk.card.questions.map((q) => (
            <li key={q.id}>{q.prompt}</li>
          ))}
        </ol>
        {/* ⚖ R1 — the two arms say DIFFERENT true things and must not be merged.
            A handback park genuinely has no door a human can use (§7-bis), so the
            first line is honest. An ANSWERABLE park held back only by the wizard's
            cap is the opposite case: the lawyer CAN answer, in the conversation,
            and telling him he cannot would be the reassuring-direction lie this
            whole chain is made of. */}
        {rehydratedAsk.answerable ? (
          <div className="mt-1.5 opacity-80">
            São mais pontos do que cabem em um cartão de cada vez. Você pode
            responder aqui na conversa: diga o que decidiu em cada ponto, na
            ordem que preferir.
          </div>
        ) : (
          <div className="mt-1.5 opacity-80">
            Ele ainda está trabalhando com elas, então não dá para respondê-las
            por aqui agora. Peça para ele retomar quando quiser.
          </div>
        )}
      </div>
    ) : null;

  // ⚖ The pill carries the ONLY destructive control (its own ✕): the
  // wizard's header ✕ PARKS (operator, 2026-08-17 — closing must not make
  // the pending indicator disappear), so permanent dismissal is a
  // deliberate second step, never the reflex close gesture.
  // ⚠ EFFECTIVE card, for the same reason `wizardActive` uses it: a rehydrated
  // ask that gets PARKED (the lawyer had text typed) must still leave the pill
  // behind. Gating this on the live card only would make a rebuilt ask vanish
  // with no way back — the exact disappearance this whole change exists to fix.
  const askUserPill =
    effectiveAskCard && !wizardActive ? (
      <div
        className={`mx-auto flex w-full ${COMPOSER_MAX_W} items-center gap-2 rounded-lg border border-cream-300/70 bg-cream-50 px-1.5 py-1 dark:border-coffee-600/70 dark:bg-coffee-900`}
      >
        <button
          type="button"
          data-testid="ask-user-pill"
          onClick={() => setParkedAskId(null)}
          className="flex flex-1 items-center gap-2 rounded px-1.5 py-0.5 text-left text-body text-coffee-800 transition-colors hover:bg-cream-100 dark:text-cream-100 dark:hover:bg-coffee-800"
        >
          <span aria-hidden="true">❓</span>
          <span>
            {effectiveAskCard.questions.length === 1
              ? "1 pergunta aguardando"
              : `${effectiveAskCard.questions.length} perguntas aguardando`}
          </span>
          <span className="ml-auto font-medium underline underline-offset-2">
            Responder
          </span>
        </button>
        <button
          type="button"
          data-testid="ask-user-pill-dismiss"
          title="Dispensar as perguntas de vez"
          onClick={() => {
            askUser.clear();
            focusComposerSoon();
          }}
          className="shrink-0 rounded p-1 text-cream-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 dark:text-coffee-500 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200"
        >
          <X size={13} />
        </button>
      </div>
    ) : null;

  // The autocomplete dropdown — absolutely positioned above the composer
  // (the host container is `relative`). Null unless a live `@` trigger is
  // open AND the workspace has files.
  const mentionPicker = pickerOpen ? (
    <MentionPicker
      listboxId={mentionListboxId}
      results={mentionResults}
      activeIndex={mentionActiveIndex}
      onSelect={insertMention}
      onHoverIndex={setMentionActiveIndex}
    />
  ) : null;

  const textarea = (
    <textarea
      ref={textareaRef}
      value={text}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      // Re-evaluate the @-trigger on caret-only moves (arrow keys, clicks,
      // Home/End) so the picker closes when the caret leaves the token
      // (finding [1]).
      onSelect={(e) =>
        reevaluateMentionTrigger(
          e.currentTarget.value,
          e.currentTarget.selectionStart ?? e.currentTarget.value.length,
        )
      }
      // Click-outside / focus-leave closes the picker (handoff §4.2;
      // finding [2]). In-picker option clicks use onMouseDown+preventDefault
      // so they DON'T blur the textarea — the pick lands before any close.
      onBlur={() => setMentionTrigger(null)}
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
      placeholder={placeholder}
      disabled={disabled}
      rows={1}
      // Editable-combobox ARIA for the @-mention autocomplete (finding [4],
      // APG combobox pattern). `role="combobox"` makes aria-expanded/
      // aria-controls/aria-activedescendant VALID on the control (they are
      // not allowed on a bare textbox); `aria-multiline` preserves the
      // multiline composer semantics under the role override. Only applied
      // when the feature is enabled — disabled embeds stay plain textboxes.
      role={enableMentions ? "combobox" : undefined}
      aria-multiline={enableMentions ? true : undefined}
      aria-autocomplete={enableMentions ? "list" : undefined}
      aria-expanded={enableMentions ? pickerOpen : undefined}
      aria-controls={pickerOpen ? mentionListboxId : undefined}
      aria-activedescendant={activeOptionId}
      className={cn(
        "min-w-0 flex-1 resize-none border-0 bg-transparent p-0 focus:outline-none",
        // Gold drop-target ring while a sidebar file is dragged over the
        // composer (handoff §4.3 visual feedback).
        isDragOver && "rounded-lg ring-2 ring-gold-500/40",
        bare
          ? // Native placeholder hidden in bare mode — the overlay
            // (FlipPlaceholderOverlay) renders the rotating placeholder
            // text with a Perplexity-style flip transition. The attribute
            // stays set for a11y; it's just visually transparent.
            // Explicit `leading-7` so the overlay (same leading) sits at
            // the same baseline — without this, browser default padding/
            // leading on `<textarea>` offsets the typed text relative to
            // the overlay placeholder.
            "text-lg leading-7 text-coffee-900 placeholder:text-transparent dark:text-cream-100 dark:placeholder:text-transparent"
          : rich
            ? "text-body text-coffee-800 placeholder:text-cream-500 dark:text-cream-100 dark:placeholder:text-coffee-500"
            : "text-xs leading-relaxed text-coffee-700 placeholder:text-cream-500 dark:text-cream-200 dark:placeholder:text-coffee-500",
      )}
      // ⚠ No `maxHeight` here on purpose — the auto-resize effect above owns
      // it, derived from the real line box. A static value would race the
      // imperative one on every re-render and flash the old ceiling.
    />
  );

  // Dictation strip — replaces the textarea row while recording /
  // transcribing (ChatGPT-style): live waveform + elapsed + ✗ / ✓.
  const dictationStrip = dictationActive ? (
    <DictationStrip dictation={dictation} onAccept={handleDictationAccept} />
  ) : null;

  // View-only (or otherwise-blocked) state: show the reason INSIDE the box
  // where the prompt would go, so it can't be mistaken for an editable —
  // but-unresponsive — composer. Wraps fully (a placeholder wouldn't).
  const inputRegion =
    disabled && disabledNotice ? (
      <div
        data-testid="chat-input-disabled-notice"
        className={cn(
          // Centred + a muted ("lighter") tone so it reads as a quiet
          // status, with an amber alert triangle to signal *why* the box
          // is inert (so it's not mistaken for a hung composer).
          "flex min-w-0 flex-1 select-none items-center justify-center gap-1.5 self-center text-center text-cream-500",
          bare ? "text-body" : rich ? "text-body-sm" : "text-xs leading-relaxed",
        )}
      >
        <AlertTriangle
          size={14}
          className="shrink-0 text-amber-500 dark:text-amber-400"
          aria-hidden
        />
        <span>{disabledNotice}</span>
      </div>
    ) : (
      textarea
    );

  // Toolbar row (rich layouts): [+] on the left; model chip + mic + send
  // on the right — the Claude-web composer layout (mic beside send, like
  // ChatGPT/Claude.ai).
  const toolbar = (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1">{attachButton}</div>
      <div className="flex items-center gap-2">
        {showModel && <ChatRescueBanner />}
        {showModel && <ChatLevelChip level={chatLevel} />}
        {micButton}
        {sendButton}
      </div>
    </div>
  );

  // Hero (bare): the parent <Card variant="hero"> owns the borderless
  // rounded-2xl filled box + padding; we render the prompt + toolbar as a
  // vertical stack. The bottom toolbar groups `[+]` (left) and model
  // chip + send (right) on the same row — Claude-web composer layout.
  // `px-1.5` on the textarea wrapper compensates for the toolbar buttons'
  // internal `p-1.5` (6px) so the placeholder text's left edge aligns
  // with the `+` icon's left edge (and the right edge with the send
  // icon's right edge).
  // L-9 (RATIFIED AS AMENDED, parent plan §3.6.0): a DEFINITIVE config break
  // blocks the composer — no silent fallback, the card names the actual fault
  // and the surface that owns it. Fail-CLOSED on definitive ONLY: loading,
  // `unknown` and transient failures all fall through to a usable composer
  // (the deliberate inversion of ChatLLMKeysGate's posture, miniplan §3(a)).
  if (levelGate) {
    return <ChatLevelConfigGate reason={levelGate.reason} detail={levelGate.detail} />;
  }

  if (bare) {
    return (
      <div className="relative flex flex-col gap-2">
        {askUserWizardHost}
        {parkedAskNotice}
        {askUserPill}
        {wizardActive ? null : (
          <>
            {mentionPicker}
            {chipsRow}
            {mentionChipsRow}
            <SelectionContextChip />
            {dictationStrip ?? (
              <div className="relative flex min-h-[44px] px-1.5 pt-2">
                {inputRegion}
                {!disabled && !text && placeholder && (
                  <FlipPlaceholderOverlay
                    text={placeholder}
                    className="text-lg leading-7 text-cream-400 dark:text-coffee-500"
                  />
                )}
              </div>
            )}
            {!dictationActive && toolbar}
          </>
        )}
      </div>
    );
  }

  // Docked: bottom-of-conversation composer. Rich docked callers (case
  // chat + global active chat) get Claude's treatment — a borderless,
  // filled, lifted rounded box at `COMPOSER_MAX_W` (720px). It no longer
  // matches the transcript: the reading column went percentage-based (80%,
  // `ChatMessageList`) on 2026-07-27 while the operator kept the promptbox at
  // its fixed size, so the two coincide only at a 900px shell. Plain callers
  // (research/editor) keep the historic full-width bordered inline row.
  if (rich) {
    return (
      <div className="px-3 pb-3 pt-1">
        <div className={`relative mx-auto flex ${COMPOSER_MAX_W} flex-col gap-2 rounded-2xl border border-cream-200/60 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(28,24,20,0.04),0_8px_24px_rgba(28,24,20,0.06)] dark:border-coffee-700/60 dark:bg-coffee-900`}>
          {askUserWizardHost}
        {parkedAskNotice}
          {askUserPill}
          {wizardActive ? null : (
            <>
              {mentionPicker}
              {chipsRow}
              {mentionChipsRow}
              <SelectionContextChip />
              {dictationStrip ?? <div className="flex">{inputRegion}</div>}
              {!dictationActive && toolbar}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-cream-200/60 p-2 dark:border-coffee-700/60">
      <div className="relative flex flex-col gap-1.5">
        {askUserWizardHost}
        {parkedAskNotice}
        {askUserPill}
        {wizardActive ? null : (
          <>
        {mentionPicker}
        {mentionChipsRow}
        <SelectionContextChip />
        {dictationStrip ?? (
          <div className="flex items-end gap-1.5 rounded-xl border border-cream-200/60 bg-cream-50 px-2.5 py-1.5 dark:border-coffee-700/60 dark:bg-coffee-900">
            {inputRegion}
            {micButton}
            {sendButton}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The dictation strip — rendered in place of the composer's input row
 * while recording / transcribing (ChatGPT-style, operator brief
 * 2026-07-02): a live waveform grows while the user talks; ✗ discards,
 * ✓ stops + transcribes (spinner while lx-stt runs). Errors (mic denied,
 * STT failure) render inline with a dismiss ✗.
 */
function DictationStrip({
  dictation,
  onAccept,
}: {
  dictation: VoiceDictation;
  onAccept: () => Promise<void>;
}) {
  const { state, levels, elapsedMs, error, cancel } = dictation;
  const secs = Math.floor(elapsedMs / 1000);
  const mm = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, "0");

  // Pad the rolling window to a fixed bar count so the strip renders a
  // stable dotted baseline that "fills up" as speech arrives (ChatGPT
  // idiom: dotted line left, live bars right).
  const bars: number[] = [
    ...Array.from({ length: Math.max(0, DICTATION_WAVE_BARS - levels.length) }, () => 0),
    ...levels,
  ];

  return (
    <div
      data-testid="dictation-strip"
      data-state={state}
      className="flex min-h-[44px] items-center gap-3 px-1.5 py-2"
    >
      {state === "error" ? (
        <div className="flex min-w-0 flex-1 items-center gap-1.5 text-body-sm text-red-600 dark:text-red-400">
          <AlertTriangle size={14} className="shrink-0" aria-hidden />
          <span className="truncate" title={error ?? undefined}>
            {error?.toLowerCase().includes("permission") || error?.toLowerCase().includes("denied")
              ? "Não foi possível acessar o microfone — verifique a permissão do navegador."
              : "Falha na transcrição. Tente novamente."}
          </span>
        </div>
      ) : state === "transcribing" ? (
        <div className="flex min-w-0 flex-1 items-center gap-2 text-body-sm text-coffee-600 dark:text-cream-300">
          <Loader2 size={14} className="shrink-0 animate-spin text-gold-600" aria-hidden />
          <span>Transcrevendo…</span>
        </div>
      ) : (
        <>
          <span className="shrink-0 text-caption tabular-nums text-cream-500 dark:text-coffee-500">
            {mm}:{ss}
          </span>
          <div
            className="flex h-8 min-w-0 flex-1 items-center gap-[2px]"
            role="img"
            aria-label="Gravando — fale agora"
            data-testid="dictation-waveform"
          >
            {bars.map((level, i) => (
              <span
                key={i}
                className={cn(
                  "w-[3px] flex-1 rounded-full transition-[height] duration-75",
                  level > 0.02
                    ? "bg-coffee-700 dark:bg-cream-200"
                    : "bg-cream-300 dark:bg-coffee-700",
                )}
                style={{ height: `${Math.max(8, level * 100)}%`, maxWidth: 4 }}
              />
            ))}
          </div>
        </>
      )}
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          data-testid="dictation-cancel"
          onClick={cancel}
          title="Descartar gravação"
          aria-label="Descartar gravação"
          className="rounded-full p-1.5 text-cream-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200"
        >
          <X size={18} />
        </button>
        {state === "recording" && (
          <button
            type="button"
            data-testid="dictation-accept"
            onClick={() => void onAccept()}
            title="Concluir e transcrever"
            aria-label="Concluir e transcrever"
            className="rounded-full bg-gold-500 p-1.5 text-coffee-900 transition-colors hover:bg-gold-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40"
          >
            <Check size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
